import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import { CategoriesWindowProps } from '../interface/CategoriesWindowProps';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import { useTranslation } from 'react-i18next';
import CategoryIcon from './CategoryIcon';
import { getCategories } from '../hooks/GetCategories';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { addMessage } from '../redux/infoMessageSlice';
import { useDispatch } from 'react-redux';
import { TranslatedCategory } from '../interface/TranslatedCategory';

const CategoriesWindow: React.FC<CategoriesWindowProps> = ({
  visibleCategoryIds,
  onToggleCategory,
  onClose,
}) => {
  const auth: string = useAuthHeader() || '';
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { t, i18n } = useTranslation();
  const [showLocal, setShowLocal] = useState(true);
  const [categories, setCategories] = useState<TranslatedCategory[]>([]);
  const isMobile = useMediaQuery('(max-width:600px)');
  const handleToggleCategory = (categoryId: number) => {
    onToggleCategory(categoryId);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await getCategories(auth);
        if (Array.isArray(result)) {
          setCategories(result);
        } else {
          dispatch(
            addMessage({
              message: 'error.failedToFetch',
              error: result,
            })
          );
        }
      } catch (error) {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: (error as Error).name,
          })
        );
      }
    };
    fetchCategories();
  }, [auth]);

  const filteredCategories = categories.filter(
    (category) => category.local === showLocal
  );

  return (
    <Box
      id={'categories-window'}
      sx={{
        position: 'absolute',
        top: 10,
        left: 10,
        padding: '10px',
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '5px',
        width: '300px',
        maxWidth: isMobile ? '70%' : '50%',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        maxHeight: '80%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t('categoriesWindow.categories')}
      </Typography>

      <Tabs
        value={showLocal}
        onChange={(_, newValue) => setShowLocal(newValue)}
        aria-label="Toggle between Local and OSM categories"
        sx={{
          alignSelf: 'center',
        }}
      >
        <Tab
          id={'category-osm'}
          label={t('categoriesWindow.public')}
          value={false}
        />
        {isAuthenticated && (
          <Tab
            id={'category-local'}
            label={t('categoriesWindow.confidential')}
            value={true}
          />
        )}
      </Tabs>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category) => (
            <Box
              key={category.id}
              sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            >
              <Box
                sx={{
                  ml: 1,
                  mr: 1,
                }}
              >
                <CategoryIcon
                  category={category.name}
                  color={category.color}
                  icon={category.icon}
                  isSelected={false}
                  clickable={false}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                }}
              >
                {i18n.language === 'de' && category.name_de
                  ? category.name_de
                  : i18n.language === 'fr' && category.name_fr
                    ? category.name_fr
                    : i18n.language === 'nl' && category.name_nl
                      ? category.name_nl
                      : category.name}
              </Typography>
              <Box
                sx={{ width: '40px', height: '40px', alignContent: 'center' }}
              >
                <IconButton
                  id={'category-visibility-button'}
                  onClick={() => handleToggleCategory(category.id)}
                >
                  {visibleCategoryIds.includes(category.id) ? (
                    <VisibilityIcon color="primary" />
                  ) : (
                    <VisibilityOffIcon color="disabled" />
                  )}
                </IconButton>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: 'gray', mt: 1 }}>
            {t('categoriesWindow.noCategory')}
          </Typography>
        )}
      </Box>

      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 5,
          right: 5,
          color: '#333',
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default CategoriesWindow;
